import { create } from 'zustand';
import { useEffect } from 'react';

interface CommandPaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

/**
 * Global store for the Command Palette (Ctrl/Cmd+K) visibility.
 * Shared across the app so any component can open/close it.
 */
export const useCommandPaletteStore = create<CommandPaletteState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
  toggle: () => set((s) => ({ open: !s.open })),
}));

/**
 * Hook that wires the Ctrl/Cmd+K keyboard shortcut to the global
 * Command Palette store. Mount once at app root.
 */
export function useCommandPalette() {
  const { open, setOpen, toggle } = useCommandPaletteStore();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        const target = e.target as HTMLElement | null;
        // Avoid hijacking shortcut while typing inside text inputs (e.g. TopHeader search)
        const tag = target?.tagName;
        const isEditable =
          tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable;
        if (isEditable) return;
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return { open, setOpen, toggle };
}
